/**
 * Общие части полей даты и периода (DateInput, DateRangeInput): разбор
 * и арифметика ISO-дат в UTC, подписи календаря, ручной ввод «ДД.ММ.ГГГГ».
 */

import { type ChangeEvent, useState } from "react";

import { formatDate } from "@/shared/lib/date";

/** «ДД.ММ.ГГГГ» → ISO, если такая дата существует; иначе null. */
export function parseTyped(text: string): string | null {
  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(text);
  if (match === null) return null;
  const iso = `${match[3] ?? ""}-${match[2] ?? ""}-${match[1] ?? ""}`;
  // 31.02 Date.UTC перенёс бы на март: сверка обратно отсекает такие даты.
  const date = parseIso(iso);
  return date !== null && toIso(date) === iso ? iso : null;
}

/** Цифры → «ДД.ММ.ГГГГ»: точка встаёт, когда за ней уже есть цифра. */
export function maskTyped(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)].filter(Boolean).join(".");
}

/** Текст поля ручного ввода и его согласование с принятым значением. */
export function useTypedDate(
  value: string,
  onChange: (value: string) => void,
  min: string | undefined,
  max: string | undefined,
) {
  const shown = (iso: string) => (iso === "" ? "" : formatDate(iso));
  const [text, setText] = useState(() => shown(value));
  const [accepted, setAccepted] = useState(value);
  // Значение сменили снаружи (календарь, очистка, соседнее поле) — текст за ним.
  if (accepted !== value) {
    setAccepted(value);
    setText(shown(value));
  }

  const iso = parseTyped(text);
  const outOfRange = iso !== null && ((min !== undefined && iso < min) || (max !== undefined && iso > max));
  const invalid = text.length === 10 && (iso === null || outOfRange);

  const change = (event: ChangeEvent<HTMLInputElement>) => {
    const next = maskTyped(event.target.value);
    setText(next);
    if (next === "") {
      if (value !== "") onChange("");
      return;
    }
    const parsed = parseTyped(next);
    if (parsed === null || parsed === value) return;
    if ((min !== undefined && parsed < min) || (max !== undefined && parsed > max)) return;
    onChange(parsed);
  };

  const blur = () => setText(shown(value));

  return { text, invalid, change, blur };
}

export const PANEL = "z-50 rounded-lg border border-line bg-raised p-3 shadow-md focus:outline-none";

export const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

const monthTitle = new Intl.DateTimeFormat("ru-RU", { month: "long", year: "numeric", timeZone: "UTC" });
export const dayTitle = new Intl.DateTimeFormat("ru-RU", {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

/** Разбор ISO-даты в полночь UTC; нераспознанное — null. */
export function parseIso(iso: string | undefined): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? "");
  if (match === null) return null;
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

export function toIso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function addDays(iso: string, days: number): string {
  const date = parseIso(iso) ?? new Date();
  date.setUTCDate(date.getUTCDate() + days);
  return toIso(date);
}

/** Тот же день через months месяцев; 31-е в коротком месяце — его последний день. */
export function addMonths(iso: string, months: number): string {
  const date = parseIso(iso) ?? new Date();
  const day = date.getUTCDate();
  const target = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
  const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(day, last));
  return toIso(target);
}


/** «Сентябрь 2026»: Intl даёт «сентябрь 2026 г.». */
export function monthName(first: Date): string {
  const plain = monthTitle.format(first).replace(/\s*г\.$/, "");
  return plain.charAt(0).toUpperCase() + plain.slice(1);
}
